import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import FilterSideBar from './FilterSideBar';
import SearchFilters from './SearchFilters';
import StudentProfilesTable from './StudentProfilesTable';
import FilterTabs from '../../../common/components/FilterTabs';
import PillMultiSelect from '../../../common/components/PillMultiSelect';
import { getInitialFilters, updateCustomMessageForm, updateExportLink } from '../../../CaseReports/components/CaseReportsV2';

const DEFAULT_STUDENT_FILTERS = {
  selected_tab: "overview",
};

const StudentsTableV2 = ({
  filter_options,
  filter_tabs,
  filtered_test_group_user_ids,
  meta_data,
  test_group,
  test_group_users,
}) => {

  const [testGroupUsers, setTestGroupUsers] = useState(test_group_users);
  const [filterTabs, setFilterTabs] = useState(filter_tabs);
  const [filters, setFilters] = useState(getInitialFilters(DEFAULT_STUDENT_FILTERS));
  const prevFiltersRef = useRef(filters);
  const prevStatusOptionsRef = useRef(filter_options.status_options);
  const [metaData, setMetaData] = useState(meta_data);
  const [showSideBar, setShowSideBar] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [statusOptions, setStatusOptions] = useState(filter_options.status_options);
  const [filteredTestGroupUserIds, setFilteredTestGroupUserIds] = useState(filtered_test_group_user_ids);

  const metaProps = {
    limit: filters.limit || metaData.per_page,
    nextPage: metaData.next_page,
    currentPage: filters.page || metaData.current_page,
    prevPage: metaData.prev_page,
    setLimit: (limit) => setFieldValue("limit", limit),
    setPage: (page) => setFieldValue("page", page),
    totalPages: metaData.total_pages,
  };

  const handleFetchTestGroupUsers = async () => {
    const response = await axios.get(`/api_v2/test_groups/${test_group.slug}/schools/student_profiles.json`,
      { params: filters }
    );

    if (response.status == 200) {
      setTestGroupUsers(response.data.test_group_users);
      setFilterTabs(response.data.filter_tabs);
      setMetaData(response.data.meta_data);
      setFilteredTestGroupUserIds(response.data.filtered_test_group_user_ids);
      setStatusOptions(response.data.filter_options.status_options);

      // Update URL without reloading the page
      const url = new URL(window.location);
      Object.keys(filters).forEach((key) => {
        url.searchParams.set(key, filters[key]);
      });
      window.history.pushState({}, "", url);

      // Update export links in erb files dynamically using the imported function
      updateExportLink("students", "export_students_link", filters, test_group);
    } else {
      toastr.error("Something went wrong while filtering the student profiles");
    }
  };

  const setFieldValue = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  useEffect(() => {
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    const statusOptionsChanged = JSON.stringify(prevStatusOptionsRef.current) !== JSON.stringify(statusOptions);

    if (filtersChanged || statusOptionsChanged) handleFetchTestGroupUsers();

    prevFiltersRef.current = { ...filters };
    prevStatusOptionsRef.current = [...statusOptions];
  }, [filters, statusOptions]);

  const handleTabClick = (value) => {
    if (value !== filters.selected_tab) {
      setFilters({
        ...filters,
        page: 1,
        pending_review: value === "pending_review",
        selected_tab: value,
        student_immunization_status: "",
      });
    }
  };

  useEffect(() => {
    updateCustomMessageForm(filters, filteredTestGroupUserIds);
  }, [testGroupUsers]);

  return (
    <>
      <FilterTabs
        filters={filters}
        handleTabClick={handleTabClick}
        options={filterTabs}
        selectedTab={filters.selected_tab}
        setFieldValue={setFieldValue}
        setFilters={setFilters}
      />
      <SearchFilters
        filterOptions={filter_options}
        filters={filters}
        setFieldValue={setFieldValue}
        setFilters={setFilters}
        setShowSideBar={() => setShowSideBar(true)}
        openStats={() => setShowStats(true)}
      />
      <PillMultiSelect
        display={showStats}
        onClose={() => setShowStats(false)}
        options={statusOptions}
        selected={filters.student_immunization_status || []}
        setSelected={(selectedStatuses) => {
          setFieldValue("student_immunization_status", selectedStatuses)
        }}
        title="STATS"
      />
      <StudentProfilesTable
        metaProps={metaProps}
        pendingReview={filters.pending_review}
        studentProfiles={testGroupUsers}
        testGroup={test_group}
      />
      <FilterSideBar
        filterOptions={{
          ...filter_options,
          status_options: statusOptions,
        }}
        filters={filters}
        onClose={() => setShowSideBar(false)}
        setFilters={setFilters}
        showSideBar={showSideBar}
      />
    </>
  );
};

export default StudentsTableV2;